import { Typography } from "@creditsafe/ui-components";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Box, Menu, MenuItem } from "@mui/material";
import { isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Button from "../../../shared/components/button/Button";
import DatePicker from "../../../shared/components/date-picker/Datepicker";
import { CenteredBox } from "../../../shared/styles/common-styles";
import type { LocaleLanguages } from "../../../utils";
import { formatDate } from "../../../utils";

const StyledErrorBox = styled(Box)`
  color: red;
  padding-left: 10px;
  padding-right: 10px;
`;

const ErrorMessageBox = styled(Box)`
  color: red;
  padding-left: 10px;
`;

const StyledMenuItem = styled(MenuItem)`
  padding-left: 10px !important;
`;

const StyledButton = styled(Button)`
  padding-left: 10px;
  margin-left: 10px;
`;

const StyledDatePicker = styled(DatePicker)`
  width: "280px";
  background-color: white;
  padding-left: "10px";
`;

export const KYCReview: React.FC<{
  header: string;
  index: string;
  updateChipLabels: (_key: string, _value: string) => void;
  selectedBothDates: (_beforeDate: string, _afterDate: string) => void;
  reviewBeforeDate: string;
  reviewAfterDate: string;
}> = ({
  header,
  index,
  updateChipLabels,
  selectedBothDates,
  reviewAfterDate,
  reviewBeforeDate,
}) => {
  const { t: translate, i18n } = useTranslation(["profile", "common"]);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const [openPopoverId, setOpenPopoverId] = React.useState<string | null>(null);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fromDateError, setFromDateError] = useState<string | null>(null);
  const [toDateError, setToDateError] = useState<string | null>(null);
  const localeLanguage = i18n.language as LocaleLanguages;

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: string
  ): void => {
    setAnchorEl(event.currentTarget);
    setOpenPopoverId(id);
  };

  useEffect(() => {
    if (reviewAfterDate !== "" && reviewBeforeDate !== "") {
      setFromDate(new Date(reviewAfterDate));
      setToDate(new Date(reviewBeforeDate));
    } else {
      setFromDate(null);
      setToDate(null);
    }
  }, [reviewAfterDate, reviewBeforeDate]);

  const clearStates = (): void => {
    setAnchorEl(null);
    setOpenPopoverId(null);
    setFromDateError(null);
    setToDateError(null);
  };

  const handlePopoverClose = (): void => {
    clearStates();
  };

  const handleFromDateChange = (date: Date | null): void => {
    setFromDate(date);
    if (toDate && date && date > toDate) {
      setFromDateError(null);
      setError(translate("common:validationMsg.fromAndToDateErrorMessage"));
    } else {
      setError(null);
      setToDateError(null);
      setFromDateError(null);
    }
  };

  const handleToDateChange = (date: Date | null): void => {
    if (fromDate && date && fromDate > date) {
      setError(translate("common:validationMsg.fromAndToDateErrorMessage"));
      setFromDateError(null);
      setToDateError(null);
    } else if (isEqual(fromDate, date)) {
      setError(
        translate("common:validationMsg.fromAndToDatesAreSameErrorMessage")
      );
      setFromDateError(null);
      setToDateError(null);
    } else {
      setError(null);
      setFromDateError(null);
      setToDateError(null);
    }
    setToDate(date);
  };

  const applyFilter = (): void => {
    if (!fromDate) {
      setFromDateError(translate("common:fromDateRequiredErrorMessage"));
    }
    if (!toDate) {
      setToDateError(translate("common:toDateRequiredErrorMessage"));
    }
    if (fromDate && toDate && !fromDateError && !toDateError && !error) {
      updateChipLabels(
        "kycReview",
        `KYC Review: ${formatDate(
          fromDate.toISOString(),
          localeLanguage
        )}-${formatDate(toDate.toISOString(), localeLanguage)}`
      );
      clearStates();
      selectedBothDates(toDate.toISOString(), fromDate.toISOString());
    }
  };

  return (
    <Box component={"span"}>
      <Typography
        key={index}
        variant="h6"
        aria-label="kyc review"
        onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
          handlePopoverOpen(e, header)
        }
      >
        <CenteredBox aria-label="kycReviewValue">
          {translate(header)}
          {openPopoverId === header ||
          reviewAfterDate !== "" ||
          reviewBeforeDate !== "" ? (
            <FilterAltIcon fontSize="small" />
          ) : (
            <FilterAltOutlinedIcon fontSize="small" />
          )}
        </CenteredBox>
      </Typography>
      <Menu
        id={`popover-${header}`}
        anchorEl={anchorEl}
        open={openPopoverId === header}
        onClose={handlePopoverClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <Typography style={{ paddingLeft: "10px" }}>
          {translate("profile:fromDate")}
        </Typography>
        <StyledMenuItem>
          <StyledDatePicker
            size="small"
            value={fromDate}
            onChange={handleFromDateChange}
            ariaLabel="fromDate"
          />
        </StyledMenuItem>
        {fromDateError && (
          <ErrorMessageBox aria-label="invalid date">
            {fromDateError}
          </ErrorMessageBox>
        )}
        <Typography style={{ paddingLeft: "10px" }}>
          {translate("profile:toDate")}
        </Typography>
        <StyledMenuItem>
          <StyledDatePicker
            size="small"
            value={toDate}
            onChange={handleToDateChange}
            ariaLabel="toDate"
          />
        </StyledMenuItem>
        {toDateError && (
          <ErrorMessageBox aria-label="invalid date">
            {toDateError}
          </ErrorMessageBox>
        )}
        {error && (
          <StyledErrorBox aria-label="invalid date">{error}</StyledErrorBox>
        )}
        <StyledButton
          variant="grey"
          aria-label="apply filter"
          disabled={false}
          onClick={applyFilter}
        >
          {translate("profile:applyFilter")}
        </StyledButton>
      </Menu>
    </Box>
  );
};
